/* eslint-disable no-void */

import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';

import vopayApi from '../../services/api.config';
import { useDataFetchIndicator } from '..';

type HookProps = {
  api: vp.Api;
};

export type AccountInvoicePdfRequest = {
  invoiceId: string;
};

export type AccountInvoicePdfResponse = {
  success: boolean;
  errorMessage: string;
  invoicePdf: string;
};

type ReturnTuple = [
  AccountInvoicePdfResponse | undefined,
  (requestParams: AccountInvoicePdfRequest) => void,
  () => void,
];

function useFetchAccountInvoicePdf({ api }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<AccountInvoicePdfRequest>();
  const [response, setResponse] = useState<AccountInvoicePdfResponse>();
  const [shouldSendRequest, setSendRequest] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  useEffect(() => {
    const sendRequest = async (requestParams: AccountInvoicePdfRequest) => {
      setDataFetchingInfo(true, 'creating pdf', 'fullScreen');

      const { Success, ErrorMessage, InvoicePDF } = await api.fetchAccountInvoicePdf(
        buildParams(requestParams),
        source,
      );

      const res = {
        success: Success,
        errorMessage: ErrorMessage,
        invoicePdf: InvoicePDF,
      };

      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams: AccountInvoicePdfRequest) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setResponse(undefined);
      setSendRequest(false);
    },
  ];

  function buildParams(requestParams: AccountInvoicePdfRequest): vp.InvoicePdfRequest {
    return {
      InvoiceID: requestParams.invoiceId,
    };
  }
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(useFetchAccountInvoicePdf);
